"use client";

import { Button } from "@universe/ui/components/button";
import { Switch } from "@universe/ui/components/switch";
import { cn } from "@universe/ui/lib/utils";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";

import { isTestEnv } from "@/lib/env";
import {
  cleanUpMockSession,
  enableMockMode,
  isBuildTimeMockMode,
  isMockModeActive,
  isRuntimeMockModeAllowed,
} from "@/lib/mockMode";
import { isLocalhost } from "@/utils/isLocalhost";
import { runWithCleanup } from "@/utils/runWithCleanup";

export const DEV_PANEL_COLLAPSED_KEY = "up-dev-panel-collapsed";

const SWITCH_ID = "dev-panel-mock-toggle";

// Lazy so the fixture data only downloads once a developer opens the panel.
const DevPanelMockUser = lazy(() =>
  import("./dev-panel-mock-user").then((m) => ({
    default: m.DevPanelMockUser,
  }))
);

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable);

/** Collapsed unless someone expanded it before; blocked storage → collapsed. */
const readCollapsedPreference = (): boolean => {
  try {
    return localStorage.getItem(DEV_PANEL_COLLAPSED_KEY) !== "false";
  } catch {
    return true;
  }
};

/** Non-prd backend, not a test build, and a local host or mock mode on. */
const readVisible = (): boolean =>
  !isTestEnv &&
  isRuntimeMockModeAllowed() &&
  (isLocalhost() || isMockModeActive());

const subscribeToNothing = () => () => {};
const serverHidden = () => false;
const serverCollapsed = () => true;

/** Async, so a synchronous throw from `enableMockMode` arrives as a rejection. */
const switchMockMode = async (next: boolean) => {
  if (next) enableMockMode();
  else await cleanUpMockSession();
};

const logSwitchFailure = (error: unknown) => {
  // Plain console, not logError: mock mode must not emit telemetry.
  console.error(
    `[mock-mode] Switching mock mode failed: ${error instanceof Error ? error.message : String(error)}`
  );
};

/**
 * Floating dev tools for toggling runtime mock mode (`lib/mockMode.ts`).
 *
 * Mounted in the root layout OUTSIDE `Providers`, so it reads storage itself
 * rather than `useMockMode()`/Redux/auth, and its copy is hardcoded English.
 * Shown only on a non-prd backend, never in test builds, and only on a local
 * host or while mock mode is already on (so a deployed UAT user can get out).
 * Storage and the host are read through `useSyncExternalStore`: hydration
 * renders the server snapshots (hidden, collapsed) so the first render is
 * `null` to match SSR, and React re-renders with the browser's values straight
 * after. They only change across a reload, so nothing is subscribed.
 */
export function DevPanel() {
  const visible = useSyncExternalStore(
    subscribeToNothing,
    readVisible,
    serverHidden
  );
  const isMock = useSyncExternalStore(
    subscribeToNothing,
    isMockModeActive,
    serverHidden
  );
  const storedCollapsed = useSyncExternalStore(
    subscribeToNothing,
    readCollapsedPreference,
    serverCollapsed
  );
  // The developer's choice this page load; until then, the stored one.
  const [collapsedChoice, setCollapsedChoice] = useState<boolean | null>(null);
  const collapsed = collapsedChoice ?? storedCollapsed;
  const switching = useRef(false);

  const setMockMode = useCallback(async (next: boolean) => {
    if (isBuildTimeMockMode() || switching.current) return;
    switching.current = true;
    // Always reload: a half-cleaned session must not keep running. The
    // failure is logged inside the task, so the log lands before the reload.
    await runWithCleanup(
      () => switchMockMode(next).catch(logSwitchFailure),
      () => window.location.assign("/")
    );
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        !(e.metaKey || e.ctrlKey) ||
        !e.shiftKey ||
        e.key.toLowerCase() !== "m" ||
        isTypingTarget(e.target)
      ) {
        return;
      }
      e.preventDefault();
      void setMockMode(!isMock);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible, isMock, setMockMode]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsedChoice(next);
    try {
      localStorage.setItem(DEV_PANEL_COLLAPSED_KEY, String(next));
    } catch {
      // Blocked storage: the choice just doesn't persist.
    }
  };

  if (!visible) return null;

  return createPortal(
    <div
      data-testid="dev-panel"
      translate="no"
      className={cn(
        "bg-background text-foreground fixed right-4 bottom-4 z-[60] flex flex-col items-stretch gap-3 rounded-xl border p-1 text-sm shadow-lg",
        !collapsed && "w-64"
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={!collapsed}
        aria-controls="dev-panel-body"
        onClick={toggleCollapsed}
        className="justify-start gap-2"
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-2 rounded-full",
            isMock ? "bg-green-500" : "bg-muted-foreground"
          )}
        />
        <span>Dev</span>
      </Button>

      {!collapsed && (
        <div id="dev-panel-body" className="flex flex-col gap-3 px-2 pb-2">
          <h2 className="font-semibold">
            <span>Dev tools</span>
          </h2>

          <div className="flex items-center justify-between gap-2">
            <label htmlFor={SWITCH_ID}>
              <span>Mock mode</span>
            </label>
            <Switch
              id={SWITCH_ID}
              data-testid="dev-panel-mock-toggle"
              checked={isMock}
              disabled={isBuildTimeMockMode()}
              onCheckedChange={(checked) => void setMockMode(checked)}
            />
          </div>

          {isBuildTimeMockMode() && (
            <p className="text-muted-foreground text-xs">
              <span>Forced on by NEXT_PUBLIC_MOCK_MODE</span>
            </p>
          )}

          <p className="text-muted-foreground text-xs">
            <span>
              {isMock ? "MSW active — no backend calls" : "Real backend"}
            </span>
          </p>

          {isMock && (
            <Suspense fallback={null}>
              <DevPanelMockUser />
            </Suspense>
          )}

          <p className="text-muted-foreground text-[10px]">
            <span>Toggle with Cmd/Ctrl + Shift + M</span>
          </p>
        </div>
      )}
    </div>,
    document.body
  );
}
